"use client";

import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import ToolLibraryAdmin from "@/components/ToolLibraryAdmin";
import UsersAdmin from "@/components/UsersAdmin";
import OdooAdmin from "@/components/OdooAdmin";

interface RAGStats {
  vector_store: {
    total_chunks: number;
    unique_documents: number;
    document_ids: string[];
  };
  memory_messages: number;
  active_sessions: number;
}

interface Session {
  _id: string;
  message_count: number;
  first_message: string;
  last_message: string;
}

interface PDFDoc {
  doc_id: string;
  doc_title: string;
  doc_name?: string;
  page_count: number;
  chunk_count: number;
  ai_summary?: string;
  web_url?: string;
  ingest_status?: string;
  ingest_error?: string;
}

interface PortalSection {
  section: string;
  title: string;
  content?: string;
  version?: number;
}

export default function AdminPage() {
  const [token, setToken] = useState<string>("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [isSuperuser, setIsSuperuser] = useState(false);

  const [activeTab, setActiveTab] = useState<"rag" | "pdf" | "content" | "generate" | "tools" | "users" | "odoo">("rag");
  const [ragStats, setRagStats] = useState<RAGStats | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pdfDocs, setPdfDocs] = useState<PDFDoc[]>([]);
  const [sections, setSections] = useState<PortalSection[]>([]);
  const [ingestStatus, setIngestStatus] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [generateSection, setGenerateSection] = useState("facilitering");
  const [generateInstructions, setGenerateInstructions] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editSection, setEditSection] = useState<PortalSection | null>(null);
  const [saving, setSaving] = useState(false);

  // Per-doc reingest state
  const [reingesting, setReingesting] = useState<Record<string, boolean>>({});
  const [reingestMsg, setReingestMsg] = useState<Record<string, string>>({});

  // Per-doc meta editing state
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingUrl, setEditingUrl] = useState("");
  const [savingDocMeta, setSavingDocMeta] = useState(false);

  // Batch reingest progress
  const [batchProgress, setBatchProgress] = useState<{
    running: boolean;
    phase: 1 | 2;
    current: string;
    currentDocId: string;
    currentIndex: number;
    done: number;
    total: number;
    phase2Done: number;
    phase2Total: number;
    log: { title: string; ok: boolean; detail: string }[];
  } | null>(null);


  // Check localStorage for existing admin session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("account_token");
    const storedUser = (() => {
      try { return JSON.parse(localStorage.getItem("account_user") || "null"); } catch { return null; }
    })();
    if (!storedToken || storedUser?.role !== "admin") {
      window.location.href = "/account?next=/admin";
      return;
    }
    setToken(storedToken);
    setUsername(storedUser.username);
    setIsSuperuser(storedUser.is_superuser ?? false);
    setLoggedIn(true);
    loadAll(storedToken);
    resumeBatchPoll(storedToken);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = (tk: string) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${tk}`,
  });

  const resumeBatchPoll = (tk: string) => {
    const poll = async () => {
      try {
        const statusRes = await fetch(`/api/admin/pdf/batch-reingest/status`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        });
        if (!statusRes.ok) return;
        const s = await statusRes.json();
        if (s.status === "idle") return;

        setBatchProgress({
          running: s.status === "running",
          phase: s.phase ?? 1,
          current: s.current ?? "",
          currentDocId: s.current_doc_id ?? "",
          currentIndex: s.current_index ?? 0,
          done: s.done ?? 0,
          total: s.total ?? 0,
          phase2Done: s.phase2_done ?? 0,
          phase2Total: s.phase2_total ?? 0,
          log: (s.log ?? []) as { title: string; ok: boolean; detail: string }[],
        });

        if (s.status === "running") {
          setTimeout(poll, 2000);
        } else {
          await loadAll(tk);
        }
      } catch { /* silent */ }
    };
    poll();
  };

  const loadAll = async (tk: string) => {
    const [statsRes, sessionsRes, pdfRes, sectionsRes] = await Promise.allSettled([
      fetch(`/api/admin/rag/stats`, { headers: authHeaders(tk) }),
      fetch(`/api/admin/rag/sessions`, { headers: authHeaders(tk) }),
      fetch(`/api/admin/pdf/list`, { headers: authHeaders(tk) }),
      fetch(`/api/admin/content/sections`, { headers: authHeaders(tk) }),
    ]);

    if (statsRes.status === "fulfilled" && statsRes.value.ok) {
      setRagStats(await statsRes.value.json());
    }
    if (sessionsRes.status === "fulfilled" && sessionsRes.value.ok) {
      const data = await sessionsRes.value.json();
      setSessions(data.sessions || []);
    }
    if (pdfRes.status === "fulfilled" && pdfRes.value.ok) {
      const data = await pdfRes.value.json();
      setPdfDocs(data.documents || []);
    }
    if (sectionsRes.status === "fulfilled" && sectionsRes.value.ok) {
      const data = await sectionsRes.value.json();
      setSections(data.sections || []);
    }
  };

  const ingestAllPdfs = async () => {
    setIngesting(true);
    setIngestStatus("Indlæser alle PDF-filer...");
    try {
      const res = await fetch(`/api/admin/pdf/ingest-all`, {
        method: "POST",
        headers: authHeaders(token),
      });
      const data = await res.json();
      const newCount = data.new ?? data.ingested?.length ?? 0;
      const skipped = data.skipped ?? 0;
      if (skipped > 0 && newCount === 0) {
        setIngestStatus(`ℹ️ Alle ${skipped} dokument(er) er allerede indlæst`);
      } else {
        setIngestStatus(`✅ Indlæst ${newCount} ny(e), ${skipped} sprunget over`);
      }
      await loadAll(token);
    } catch {
      setIngestStatus("❌ Fejl ved indlæsning");
    } finally {
      setIngesting(false);
    }
  };

  const deleteSession = async (sessionId: string) => {
    await fetch(`/api/admin/rag/session/${sessionId}`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    await loadAll(token);
  };

  const deletePdf = async (docId: string, docTitle: string) => {
    if (!confirm(`Slet "${docTitle}" fra vektordatabasen?\n\nDenne handling kan ikke fortrydes.`)) return;
    try {
      const res = await fetch(`/api/admin/pdf/${docId}`, {
        method: "DELETE",
        headers: authHeaders(token),
      });
      if (res.ok) {
        setIngestStatus(`🗑️ "${docTitle}" slettet`);
        await loadAll(token);
      } else {
        setIngestStatus(`❌ Kunne ikke slette "${docTitle}"`);
      }
    } catch {
      setIngestStatus("❌ Fejl ved sletning");
    }
  };

  const startEditDoc = (doc: PDFDoc) => {
    setEditingDocId(doc.doc_id);
    setEditingTitle(doc.doc_title);
    setEditingUrl(doc.web_url ?? "");
  };

  const cancelEditDoc = () => setEditingDocId(null);

  const saveDocMeta = async (docId: string) => {
    setSavingDocMeta(true);
    try {
      const res = await fetch(`/api/admin/pdf/${docId}/meta`, {
        method: "PATCH",
        headers: authHeaders(token),
        body: JSON.stringify({
          doc_title: editingTitle.trim() || undefined,
          web_url: editingUrl.trim() || "",
        }),
      });
      if (res.ok) {
        setEditingDocId(null);
        await loadAll(token);
      }
    } finally {
      setSavingDocMeta(false);
    }
  };

  const reingestDoc = async (docId: string, docTitle: string) => {
    setReingesting((p) => ({ ...p, [docId]: true }));
    setReingestMsg((p) => ({ ...p, [docId]: "⏳ Nulstiller og genindlæser..." }));
    try {
      const res = await fetch(`/api/admin/pdf/${docId}/reingest`, {
        method: "POST",
        headers: authHeaders(token),
      });
      if (res.ok) {
        const data = await res.json();
        setReingestMsg((p) => ({ ...p, [docId]: `✅ Klar – ${data.chunks ?? 0} segmenter` }));
        await loadAll(token);
      } else {
        const err = await res.json().catch(() => ({}));
        setReingestMsg((p) => ({ ...p, [docId]: `❌ ${err.detail ?? "Fejl"}` }));
      }
    } catch {
      setReingestMsg((p) => ({ ...p, [docId]: "❌ Forbindelsesfejl" }));
    } finally {
      setReingesting((p) => ({ ...p, [docId]: false }));
    }
  };

  const migrateToUuid = async () => {
    if (!confirm("Tildel UUID-identifikatorer til alle dokumenter med filnavn-baserede id'er?\n\nKør '⚡ Regenerer alle' bagefter for at genopbygge Chroma-vektorer.")) return;
    try {
      const res = await fetch(`/api/admin/pdf/migrate-to-uuid`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setIngestStatus(`🔑 Migreret ${data.migrated} dok. (${data.already_uuid} allerede UUID)`);
      loadAll(token);
    } catch {
      setIngestStatus("❌ UUID migration fejlede");
    }
  };

  const batchReingest = async () => {
    if (!confirm(`Vil du nulstille og regenerere AL viden for ALLE ${pdfDocs.length} dokumenter?\n\nDette kan tage lang tid. Processen kører i baggrunden og kan ikke afbrydes.`)) return;

    const startRes = await fetch(`/api/admin/pdf/batch-reingest`, {
      method: "POST",
      headers: authHeaders(token),
    }).catch(() => null);
    if (!startRes || !startRes.ok) {
      setBatchProgress((p) => p ? { ...p, running: false, current: "❌ Kunne ikke starte job" } : { running: false, phase: 1, current: "❌ Kunne ikke starte job", currentDocId: "", currentIndex: 0, done: 0, total: 0, phase2Done: 0, phase2Total: 0, log: [] });
      return;
    }
    const startData = await startRes.json();
    if (startData.status === "already_running") {
      // Job already running — just start polling
    }

    setBatchProgress({ running: true, phase: 1, current: "", currentDocId: "", currentIndex: 0, done: 0, total: startData.total ?? pdfDocs.length, phase2Done: 0, phase2Total: 0, log: [] });

    // Poll until completed
    const poll = async () => {
      try {
        const statusRes = await fetch(`/api/admin/pdf/batch-reingest/status`, {
          headers: authHeaders(token),
        });
        if (!statusRes.ok) return;
        const s = await statusRes.json();
        if (s.status === "idle") return;

        setBatchProgress({
          running: s.status === "running",
          phase: s.phase ?? 1,
          current: s.current ?? "",
          currentDocId: s.current_doc_id ?? "",
          currentIndex: s.current_index ?? 0,
          done: s.done ?? 0,
          total: s.total ?? 0,
          phase2Done: s.phase2_done ?? 0,
          phase2Total: s.phase2_total ?? 0,
          log: (s.log ?? []) as { title: string; ok: boolean; detail: string }[],
        });

        if (s.status === "running") {
          setTimeout(poll, 2000);
        } else {
          // Completed — refresh PDF list to show updated ingest_status
          await loadAll(token);
        }
      } catch {
        setTimeout(poll, 3000);
      }
    };
    setTimeout(poll, 1000);
  };

  const clearAllMemory = async () => {
    if (!confirm("Er du sikker på at slette AL RAG-hukommelse?")) return;
    await fetch(`/api/admin/rag/all`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    await loadAll(token);
  };

  const generateContent = async () => {
    setGenerating(true);
    setGeneratedContent("");
    try {
      const res = await fetch(`/api/admin/content/regenerate`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          section: generateSection,
          instructions: generateInstructions,
          base_on_rag: true,
        }),
      });
      const data = await res.json();
      setGeneratedContent(data.generated_content || "");
    } catch {
      setGeneratedContent("Fejl ved generering");
    } finally {
      setGenerating(false);
    }
  };

  const saveGeneratedContent = async () => {
    if (!generatedContent) return;
    setSaving(true);
    try {
      const sectionData = sections.find((s) => s.section === generateSection);
      await fetch(`/api/admin/content/section`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({
          section: generateSection,
          title: sectionData?.title || generateSection,
          content: generatedContent,
          notes: `AI genereret: ${new Date().toLocaleDateString("da-DK")}`,
        }),
      });
      await loadAll(token);
      setGeneratedContent("");
    } finally {
      setSaving(false);
    }
  };

  // Pre-computed batch progress display values (avoids nested template-literals in JSX)
  const bpTotal = batchProgress?.total ?? 0;
  const bpDone = batchProgress?.done ?? 0;
  const bpIndex = batchProgress?.currentIndex ?? 0;
  const bpRunning = batchProgress?.running ?? false;
  const bpPhase = batchProgress?.phase ?? 1;
  const bpPhase2Done = batchProgress?.phase2Done ?? 0;
  const bpPhase2Total = batchProgress?.phase2Total ?? 0;
  const bpBarNumerator = bpPhase === 1
    ? (bpRunning ? bpIndex - 1 : bpDone)
    : bpPhase2Done;
  const bpBarTotal = bpPhase === 1 ? bpTotal : (bpPhase2Total || 1);
  const bpBarPct = bpBarTotal > 0 ? String(Math.round((bpBarNumerator / bpBarTotal) * 100)) + "%" : "0%";
  const bpPhaseLabel = bpPhase === 2 ? " (Fase 2: perspektivoversigter)" : "";
  const bpCounterText = bpRunning
    ? (bpPhase === 1
      ? bpIndex + " af " + bpTotal + " (" + bpDone + " klar)"
      : bpPhase2Done + " af " + bpPhase2Total + " perspektiver")
    : bpDone + " / " + bpTotal + " behandlet";
  const bpCurrentLabel = batchProgress?.current
    ? (bpRunning ? (bpPhase === 1 ? "\u23f3 " : "\ud83e\udde0 ") + batchProgress.current : batchProgress.current)
    : "";

  if (!loggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-400 text-sm">Omdirigerer til login...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚙️</span>
            <div>
              <h1 className="font-bold text-gray-900">Admin Panel</h1>
              <p className="text-xs text-gray-500">Lokalt Innovations hus i Haslev by — RAG Manager</p>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <span className="text-sm text-gray-500">Logget ind som <strong>{username}</strong></span>
            <a href="/" className="text-sm text-green-700 hover:text-green-800 underline">← Portal</a>
            <button onClick={() => {
              localStorage.removeItem("account_token");
              localStorage.removeItem("account_user");
              window.location.href = "/account";
            }} className="text-sm text-red-600 hover:text-red-700">Log ud</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats overview */}
        {ragStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Vektorsegmenter", value: ragStats.vector_store.total_chunks, icon: "🔢" },
              { label: "Indekserede dokumenter", value: ragStats.vector_store.unique_documents, icon: "📄" },
              { label: "RAG-beskeder", value: ragStats.memory_messages, icon: "💬" },
              { label: "Sessioner", value: ragStats.active_sessions, icon: "👥" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span>{stat.icon}</span>
                  <span className="text-xs text-gray-500">{stat.label}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
          {[
            { id: "rag" as const, label: "🧠 RAG Hukommelse" },
            { id: "pdf" as const, label: "📄 PDF Manager" },
            { id: "content" as const, label: "📝 Indhold" },
            { id: "generate" as const, label: "✨ AI Generator" },
            { id: "tools" as const, label: "🔧 Bibliotek" },
            { id: "users" as const, label: "👥 Brugere" },
            { id: "odoo" as const, label: "🔗 Odoo" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* RAG Memory Tab */}
        {activeTab === "rag" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Samtale-sessioner</h2>
              <button
                onClick={clearAllMemory}
                className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm hover:bg-red-100"
              >
                🗑️ Ryd al hukommelse
              </button>
            </div>

            {sessions.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                Ingen sessioner endnu
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Session ID</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Beskeder</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Seneste</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500 truncate max-w-48">
                          {s._id}
                        </td>
                        <td className="px-4 py-3 font-semibold">{s.message_count}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(s.last_message).toLocaleDateString("da-DK")}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => deleteSession(s._id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Slet
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* PDF Manager Tab */}
        {activeTab === "pdf" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">PDF Dokumenter</h2>
              <div className="flex gap-3 items-center">
                {ingestStatus && <span className="text-sm text-gray-600">{ingestStatus}</span>}
                <button
                  onClick={migrateToUuid}
                  disabled={batchProgress?.running || ingesting}
                  className="px-4 py-2 bg-purple-50 text-purple-800 border border-purple-200 rounded-xl text-sm font-medium hover:bg-purple-100 disabled:opacity-40"
                  title="Tildel UUID-identifikatorer til dokumenter med filnavn-baserede id'er"
                >
                  🔑 UUID migration
                </button>
                <button
                  onClick={batchReingest}
                  disabled={batchProgress?.running || ingesting}
                  className="px-4 py-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-xl text-sm font-medium hover:bg-amber-100 disabled:opacity-40"
                >
                  {batchProgress?.running ? `⏳ ${batchProgress.currentIndex}/${batchProgress.total}` : "⚡ Regenerer alle"}
                </button>
                <button
                  onClick={ingestAllPdfs}
                  disabled={ingesting || batchProgress?.running}
                  className="px-4 py-2 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: "#2d6a4f" }}
                >
                  {ingesting ? "⏳ Indlæser..." : "🔄 Indlæs alle PDF'er"}
                </button>
              </div>
            </div>

            {pdfDocs.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                <div className="text-3xl mb-2">📭</div>
                <p>Ingen PDF-dokumenter i vektordatabasen.</p>
                <p className="text-sm mt-1">Klik &quot;Indlæs alle PDF&#39;er&quot; for at importere fra {"/PDF/"}-mappen.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {pdfDocs.map((doc) => (
                  <div
                    key={doc.doc_id}
                    className={`rounded-xl border p-4 transition-colors ${
                      batchProgress?.running && batchProgress.currentDocId === doc.doc_id
                        ? "bg-amber-50 border-amber-300 ring-2 ring-amber-300"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {editingDocId === doc.doc_id ? (
                          /* ── Inline edit mode ── */
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveDocMeta(doc.doc_id); if (e.key === "Escape") cancelEditDoc(); }}
                              className="w-full border border-blue-300 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-900 focus:outline-none focus:border-blue-500"
                              placeholder="Dokumenttitel"
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 shrink-0">🔗 URL</span>
                              <input
                                type="url"
                                value={editingUrl}
                                onChange={(e) => setEditingUrl(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Escape") cancelEditDoc(); }}
                                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-blue-400"
                                placeholder="https://..."
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveDocMeta(doc.doc_id)}
                                disabled={savingDocMeta || !editingTitle.trim()}
                                className="px-3 py-1 text-xs text-white rounded-lg disabled:opacity-40"
                                style={{ backgroundColor: "#2d6a4f" }}
                              >
                                {savingDocMeta ? "Gemmer..." : "💾 Gem"}
                              </button>
                              <button
                                onClick={cancelEditDoc}
                                className="px-3 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                              >
                                Annuller
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* ── Display mode ── */
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900">{doc.doc_title}</h3>
                              {batchProgress?.running && batchProgress.currentDocId === doc.doc_id && (
                                <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full animate-pulse">
                                  ⏳ Behandler...
                                </span>
                              )}
                            </div>
                            <div className="flex gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                              <span>📄 {doc.page_count} sider</span>
                              <span>🔢 {doc.chunk_count} segmenter</span>
                              {doc.doc_name && <span className="text-gray-400 truncate" title={doc.doc_name}>📁 {doc.doc_name}</span>}
                              <span className="font-mono text-gray-300 truncate" title={doc.doc_id}>🔑 {doc.doc_id.length === 36 ? doc.doc_id.slice(0, 8) + "…" : doc.doc_id}</span>
                              {doc.ingest_status === "failed" && (
                                <span
                                  className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium"
                                  title={doc.ingest_error ?? "Ukendt fejl"}
                                >
                                  ⚠️ Fejlet genindlæsning
                                </span>
                              )}
                            </div>
                            {doc.web_url && (
                              <div className="mt-1">
                                <a
                                  href={doc.web_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                >
                                  🔗 {doc.web_url.replace(/^https?:\/\//, "").slice(0, 60)}{doc.web_url.length > 67 ? "..." : ""}
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                              </div>
                            )}
                            {doc.ai_summary && (
                              <div className="mt-2 text-sm text-gray-600 leading-relaxed">
                                <ReactMarkdown
                                  components={{
                                    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                    ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
                                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
                                    li: ({ children }) => <li>{children}</li>,
                                    h1: ({ children }) => <h1 className="font-bold mt-1">{children}</h1>,
                                    h2: ({ children }) => <h2 className="font-semibold mt-1">{children}</h2>,
                                    h3: ({ children }) => <h3 className="font-medium mt-1">{children}</h3>,
                                  }}
                                >
                                  {doc.ai_summary}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>
                        )}
                      </div>{/* end flex-1 */}
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {editingDocId !== doc.doc_id && (
                          <button
                            onClick={() => startEditDoc(doc)}
                            disabled={batchProgress?.running}
                            className="px-3 py-1.5 text-xs text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-40"
                            title="Rediger titel og web-reference"
                          >
                            ✏️ Rediger
                          </button>
                        )}
                        <button
                          onClick={() => reingestDoc(doc.doc_id, doc.doc_title)}
                          disabled={reingesting[doc.doc_id] || batchProgress?.running}
                          className="px-3 py-1.5 text-xs text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-40"
                          title="Nulstil cached viden og genindlæs fra PDF"
                        >
                          {reingesting[doc.doc_id] ? "⏳" : "🔄"} Nulstil viden
                        </button>
                        <button
                          onClick={() => deletePdf(doc.doc_id, doc.doc_title)}
                          className="px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                          title="Slet fra vektordatabase"
                        >
                          🗑️ Slet
                        </button>
                        {reingestMsg[doc.doc_id] && (
                          <span className="text-xs text-gray-500 text-right">{reingestMsg[doc.doc_id]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Batch progress panel */}
            {batchProgress && (
              <div className="bg-white rounded-xl border border-amber-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    ⚡ Batch regenerering{bpPhaseLabel}
                    {batchProgress.running && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full animate-pulse">
                        {bpPhase === 2 ? "Fase 2..." : "Kører..."}
                      </span>
                    )}
                    {!batchProgress.running && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Færdig</span>
                    )}
                  </h3>
                  {!batchProgress.running && (
                    <button onClick={() => setBatchProgress(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕ Luk</button>
                  )}
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: bpBarPct }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mb-3">
                  <span>{bpCurrentLabel}</span>
                  <span>{bpCounterText}</span>
                </div>

                {/* Log */}
                {batchProgress.log.length > 0 && (
                  <div className="max-h-48 overflow-y-auto rounded-lg bg-gray-50 border border-gray-100 divide-y divide-gray-100">
                    {[...batchProgress.log].reverse().map((entry, i) => (
                      <div key={i} className={`px-3 py-1.5 flex items-center gap-2 text-xs ${entry.ok ? "text-gray-700" : "text-red-600"}`}>
                        <span>{entry.ok ? "✅" : "❌"}</span>
                        <span className="font-medium flex-1 truncate">{entry.title}</span>
                        <span className="text-gray-400">{entry.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Portal Sektioner</h2>
            <div className="grid gap-4">
              {sections.map((s) => (
                <div key={s.section} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{s.title}</h3>
                        {s.version && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                            v{s.version}
                          </span>
                        )}
                      </div>
                      {s.content && (
                        <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                          {s.content.slice(0, 200)}{s.content.length > 200 ? "..." : ""}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setEditSection(s)}
                      className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                    >
                      Rediger
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Edit dialog */}
            {editSection && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-2xl p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Rediger: {editSection.title}</h3>
                  <textarea
                    value={editSection.content || ""}
                    onChange={(e) => setEditSection({ ...editSection, content: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl p-3 text-sm h-48 resize-none focus:outline-none focus:border-green-400"
                  />
                  <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={() => setEditSection(null)} className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                      Annuller
                    </button>
                    <button
                      onClick={async () => {
                        if (!editSection) return;
                        setSaving(true);
                        await fetch(`/api/admin/content/section`, {
                          method: "PUT",
                          headers: authHeaders(token),
                          body: JSON.stringify({
                            section: editSection.section,
                            title: editSection.title,
                            content: editSection.content,
                          }),
                        });
                        await loadAll(token);
                        setEditSection(null);
                        setSaving(false);
                      }}
                      disabled={saving}
                      className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                      style={{ backgroundColor: "#2d6a4f" }}
                    >
                      {saving ? "Gemmer..." : "Gem"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Generator Tab */}
        {activeTab === "generate" && (
          <div className="space-y-6">
            <h2 className="text-lg font-bold text-gray-900">AI Indholdsgeneration</h2>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sektion</label>
                  <select
                    value={generateSection}
                    onChange={(e) => setGenerateSection(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  >
                    <option value="facilitering">Facilitering af Partnerskaber</option>
                    <option value="information">Målrettet Information</option>
                    <option value="groenne_vaerktoejer">Grøn Livsstil og Transport</option>
                    <option value="monitorering">Monitorering og Gennemsigtighed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instruktioner (valgfri)
                  </label>
                  <textarea
                    value={generateInstructions}
                    onChange={(e) => setGenerateInstructions(e.target.value)}
                    placeholder="F.eks.: Fokuser på konkrete handlinger for borgere..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:border-green-400"
                  />
                </div>

                <button
                  onClick={generateContent}
                  disabled={generating}
                  className="px-6 py-2.5 text-white rounded-xl text-sm font-medium disabled:opacity-50 w-fit"
                  style={{ backgroundColor: "#2d6a4f" }}
                >
                  {generating ? "⏳ Genererer med AI..." : "✨ Generer indhold med AI"}
                </button>
              </div>
            </div>

            {generatedContent && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  ✨ Genereret indhold
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">AI</span>
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-4">
                  {generatedContent}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={saveGeneratedContent}
                    disabled={saving}
                    className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                    style={{ backgroundColor: "#2d6a4f" }}
                  >
                    {saving ? "Gemmer..." : "💾 Gem som ny version"}
                  </button>
                  <button
                    onClick={() => setGeneratedContent("")}
                    className="px-5 py-2 text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Kassér
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Tool Library Admin Tab */}
        {activeTab === "tools" && (
          <ToolLibraryAdmin token={token} />
        )}

        {/* Users & Account Management Tab */}
        {activeTab === "users" && (
          <UsersAdmin token={token} isSuperuser={isSuperuser} />
        )}

        {/* Odoo Integration Tab */}
        {activeTab === "odoo" && (
          <OdooAdmin token={token} />
        )}
      </div>
    </div>
  );
}
